import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Badge, Button, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import ContentLayout from '../../users/components/ContentLayout';
import GenericTable from '../../../components/tables/GenericTable';
import TableLoadingArea from '../../../components/tables/TableLoadingArea';
import Pagination from '../../../layout/Pagination';
import ConnectionIndicator from '../components/ConnectionIndicator';
import ChatModal from '../components/ChatModal';
import NewMessageModal from '../components/NewMessageModal';
import { useSocket } from '../../../context/useSocket';
import { useChatNotifications } from '../context/useChatNotifications';
import { useDebounce } from '../../../shared/hooks/useDebounce';
import { fetchConversations } from '../api/chatApi';
import {
  sortConversations,
  upsertConversation,
  clearConversationUnread,
} from '../utils/conversationUtils';
import { formatConversationTime } from '../utils/formatMessageTime';
import { formatNameLastFirst } from '../../../shared/utils/formatName';
import styles from '../chat.module.scss';

const LIST_LIMIT = 20;

// Koliko karaktera poslednje poruke prikazujemo u tabeli pre skracivanja. CSS
// (fiksna sirina kolone + ellipsis) drzi kolonu mirnom, a ovo dodatno ogranicava
// tekst da red ne bude neopravdano visok kod dugackih poruka.
const MAX_LAST_MESSAGE_CHARS = 90;
const truncateMessage = (text) => {
  const value = String(text ?? '');
  return value.length > MAX_LAST_MESSAGE_CHARS
    ? `${value.slice(0, MAX_LAST_MESSAGE_CHARS).trimEnd()}…`
    : value;
};

// Ikonica papirnog aviona (asocira na slanje poruke) za dugme otvaranja chata.
const PaperPlaneIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M22 2 11 13" />
    <path d="M22 2 15 22l-4-9-9-4 20-7z" />
  </svg>
);

const MessagesPage = () => {
  const { socket } = useSocket();
  const {
    markConversationRead: clearNavUnread,
    setActiveConversation: setActiveNotifConversation,
  } = useChatNotifications();

  const [conversations, setConversations] = useState([]);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [activeConversation, setActiveConversation] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isNewMessageOpen, setIsNewMessageOpen] = useState(false);

  const latestRequestRef = useRef(0);
  // Trenutni (debounced) upit drzimo i u ref-u da socket handler moze da ga cita
  // bez ponovnog pretplacivanja pri svakom kucanju.
  const searchRef = useRef('');
  const trimmedSearch = debouncedSearch.trim();
  useEffect(() => {
    searchRef.current = trimmedSearch;
  }, [trimmedSearch]);

  const loadConversations = useCallback(async (page, searchTerm) => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    setListLoading(true);

    const { data, ok, message } = await fetchConversations({
      page,
      limit: LIST_LIMIT,
      search: searchTerm,
    });

    if (requestId !== latestRequestRef.current) return;
    setListLoading(false);

    if (!ok || !data) {
      setErrorMessage(message || 'Neuspesno ucitavanje razgovora.');
      return;
    }

    setErrorMessage('');
    const items = Array.isArray(data.items) ? data.items : [];
    setConversations(sortConversations(items));

    const total = typeof data.total === 'number' ? data.total : items.length;
    const limit = data.limit || LIST_LIMIT;
    setTotalResults(total);
    setTotalPages(Math.max(1, Math.ceil(total / limit)));
  }, []);

  useEffect(() => {
    loadConversations(currentPage, trimmedSearch);
  }, [loadConversations, currentPage, trimmedSearch]);

  // Napustanje stranice Poruke ponisti "aktivan razgovor" (inace bi mu se poruke
  // i dalje potiskivale iz brojaca iako chat vise nije otvoren).
  useEffect(
    () => () => setActiveNotifConversation(null),
    [setActiveNotifConversation]
  );

  // Kad se pretraga promeni, vrati se na prvu stranicu (preskoci prvi prolaz da
  // ne resetuje stranicu bez potrebe pri montiranju).
  const searchResetSkipRef = useRef(true);
  useEffect(() => {
    if (searchResetSkipRef.current) {
      searchResetSkipRef.current = false;
      return;
    }
    setCurrentPage(1);
  }, [trimmedSearch]);

  // Live: upsert reda i presortiranje kad stigne conversation:updated. Dok je
  // pretraga aktivna, ne ubacujemo nepoznate razgovore (mogli bi da ne odgovaraju
  // upitu) - samo azuriramo one koji su vec u filtriranoj listi.
  useEffect(() => {
    if (!socket) return undefined;

    const handleConversationUpdated = (conversation) => {
      if (!conversation?.id) return;
      const insertIfMissing = !searchRef.current;
      setConversations((prev) =>
        upsertConversation(prev, conversation, { insertIfMissing })
      );
    };

    socket.on('conversation:updated', handleConversationUpdated);
    return () => socket.off('conversation:updated', handleConversationUpdated);
  }, [socket]);

  const openChat = useCallback(
    (conversation) => {
      setActiveConversation(conversation);
      // Obelezi ovaj razgovor kao aktivan (otvoren) da mu se nove poruke ne
      // beleze kao neprocitane dok se apoteka dopisuje sa njim.
      setActiveNotifConversation(conversation.id);
      setIsChatOpen(true);
      // Lokalno obrisi "neprocitano" odmah; server takodje emituje update.
      setConversations((prev) => clearConversationUnread(prev, conversation.id));
      // Očisti i globalni badge na "Poruke" tabu odmah.
      clearNavUnread(conversation.id);
    },
    [clearNavUnread, setActiveNotifConversation]
  );

  const closeChat = useCallback(() => setIsChatOpen(false), []);
  const handleAfterClose = useCallback(() => {
    setActiveConversation(null);
    // Razgovor vise nije otvoren -> od sada mu se nove poruke ponovo broje.
    setActiveNotifConversation(null);
  }, [setActiveNotifConversation]);

  // Apoteka pokrece razgovor: backend vrati (get-or-create) razgovor, ubacimo
  // ga u listu i odmah otvorimo chat.
  const handleConversationReady = useCallback(
    (conversation) => {
      setConversations((prev) => upsertConversation(prev, conversation));
      setIsNewMessageOpen(false);
      openChat(conversation);
    },
    [openChat]
  );

  const columns = useMemo(
    () => [
      {
        key: 'patientName',
        label: 'Pacijent',
        width: 240,
        customCell: (row) => {
          const unread = row.pharmacyUnreadCount > 0;
          return (
            <button
              type="button"
              className={styles.patientCell}
              onClick={() => openChat(row)}
            >
              <span>{formatNameLastFirst(row, '') || 'Nepoznat pacijent'}</span>
              {unread && (
                <Badge
                  className={styles.unreadBadge}
                  count={row.pharmacyUnreadCount}
                  overflowCount={99}
                />
              )}
            </button>
          );
        },
      },
      {
        key: 'lastMessage',
        label: 'Poslednja poruka',
        customCell: (row) => {
          if (!row.lastMessage) {
            return (
              <span className={styles.lastMessage__empty}>Nema poruka</span>
            );
          }
          return (
            <span className={styles.lastMessage}>
              <span
                className={styles.lastMessage__text}
                title={row.lastMessage.content}
              >
                {truncateMessage(row.lastMessage.content)}
              </span>
              <span className={styles.lastMessage__time}>
                {formatConversationTime(
                  row.lastMessageAt || row.lastMessage.createdAt
                )}
              </span>
            </span>
          );
        },
      },
      {
        key: 'lastContact',
        label: 'Poslednji kontakt',
        width: 220,
        customCell: (row) => {
          // Poslednji apotekar/admin koji je pisao. Koristi `lastPharmacyMessage`
          // ako ga backend salje; u suprotnom padni na poslednju poruku kada je
          // ona sa strane apoteke.
          const staff =
            row.lastPharmacyMessage ||
            (row.lastMessage?.senderSide === 'pharmacy'
              ? row.lastMessage
              : null);

          if (!staff?.senderName) {
            return <span className={styles.lastMessage__empty}>—</span>;
          }

          return (
            <span className={styles.lastMessage}>
              <span className={styles.lastContact__name}>
                {staff.senderName}
              </span>
              <span className={styles.lastMessage__time}>
                {formatConversationTime(staff.createdAt)}
              </span>
            </span>
          );
        },
      },
      {
        key: 'open',
        label: '',
        align: 'alignRight',
        width: 72,
        customCell: (row) => (
          <button
            type="button"
            className={styles.openButton}
            onClick={() => openChat(row)}
            aria-label="Otvori razgovor"
            title="Otvori razgovor"
          >
            <PaperPlaneIcon />
          </button>
        ),
      },
    ],
    [openChat]
  );

  return (
    <>
      <ContentLayout>
        <div className={styles.toolbar}>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            className={styles.toolbar__search}
            placeholder="Pretraži po imenu i prezimenu"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className={styles.toolbar__actions}>
            <ConnectionIndicator />
            <Button type="primary" onClick={() => setIsNewMessageOpen(true)}>
              Nova poruka
            </Button>
          </div>
        </div>

        <TableLoadingArea
          loading={listLoading}
          isEmpty={conversations.length === 0}
        >
          <GenericTable
            data={conversations}
            columns={columns}
            rowKey="id"
            fixedLayout
            rowClassName={(row) =>
              row.pharmacyUnreadCount > 0 ? 'chat-row-unread' : undefined
            }
            emptyState={{
              icon: '/assets/icons/no-users.svg',
              title: trimmedSearch
                ? 'Nema razgovora za zadatu pretragu'
                : 'Trenutno nema razgovora',
            }}
          />
        </TableLoadingArea>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalResults={totalResults}
          onPageChange={setCurrentPage}
          loading={listLoading}
        />

        {!!errorMessage && (
          <span style={{ display: 'none' }}>{errorMessage}</span>
        )}
      </ContentLayout>

      <NewMessageModal
        open={isNewMessageOpen}
        onClose={() => setIsNewMessageOpen(false)}
        onConversationReady={handleConversationReady}
      />

      <ChatModal
        open={isChatOpen}
        conversation={activeConversation}
        onClose={closeChat}
        afterClose={handleAfterClose}
      />
    </>
  );
};

export default MessagesPage;
